// StationData: the station's data layer in one object, for the app shell and the station
// screens. It owns the engine's connection, the device credentials, staff sign-in and the
// derived states the UI shows (pairing, minimum-version gate, entitlement banner, clock
// skew), and turns a station action into a command in the outbox.

import {
  type CommandEnvelope,
  type DevicePlatform,
  type DocPrefix,
  type Ed25519Subtle,
  type PermissionKey,
  type UpgradeNotice,
  approverPermission,
  isStoredStatus,
  newId,
} from '@enp/domain';
import type { ApiClient } from './api.js';
import {
  BUSINESS_DAY_SQL,
  type BusinessDayRow,
  type BusinessDaySettings,
  type BusinessDayState,
  DEFAULT_BUSINESS_DAY,
  businessDayAt,
  businessDaySettings,
} from './business-day.js';
import {
  DeviceAuth,
  type PairInput,
  type PairedDevice,
  type PairingState,
  RepairRequiredError,
  UpgradeRequiredError,
} from './credentials.js';
import { type ClockSkew, readStoredConfig, refreshDeviceConfig } from './device-config.js';
import {
  type LocalEffect,
  type LocalTx,
  ORDERS_HANDOVER_STREAM,
  PRODUCTION_DONE_STREAM,
  RECEIVING_DONE_STREAM,
  type SyncConnector,
  type SyncEngine,
  type SyncEngineStatus,
} from './engine.js';
import { type EntitlementState, evaluateEntitlement } from './entitlements.js';
import { pruneHandoverActions } from './handover.js';
import {
  pruneResolvedBatchActions,
  RESOLVED_HELD_BATCH_ACTIONS_SQL,
  RESOLVED_HELD_HANDOVERS_SQL,
  resolveHeldActions,
} from './held-actions.js';
import { initDeviceSequence, nextDocNumber } from './numbering.js';
import {
  acknowledgeFlagged,
  acknowledgeRejected,
  pruneResolvedHolds,
  RESOLVED_HOLDS_SQL,
} from './outcomes.js';
import { InvalidCommandError, STATION_REGISTRY } from './outbox.js';
import { RESOLVED_HELD_RECEIPTS_SQL, resolveHeldReceipts } from './receiving.js';
import { type SecureStore, assertSecureStoreFor } from './secure-store.js';
import {
  type ApprovalResult,
  type SignInResult,
  type StaffMember,
  type StaffSession,
  StaffSignIn,
  sessionCan,
} from './staff.js';
import {
  type UpgradeGate,
  createCommandUploader,
  raiseUpgradeGate,
  readUpgradeGate,
} from './upload.js';
import { Store, errorMessage } from './util.js';

export interface UpgradeState {
  required: boolean;
  minAppVersion?: string;
  reason?: UpgradeGate['reason'];
}

export interface StationDataStatus {
  engine: SyncEngineStatus;
  pairing: PairingState;
  upgrade: UpgradeState;
  entitlement: EntitlementState;
  clockSkew: ClockSkew | null;
  /**
   * The business's zone, trading-day cut-off and today's trading day (B-051): from the synced
   * account row, SAST and midnight until it arrives. `today` moves on at the cut-off.
   */
  businessDay: BusinessDayState;
  session: StaffSession | null;
  /** Why the last config refresh failed (offline is normal and not reported). */
  configProblem: string | null;
}

export interface StationDataOptions {
  engine: SyncEngine;
  api: ApiClient;
  store: SecureStore;
  platform: DevicePlatform;
  appVersion: string;
  /** The sync service URL; without one, only commands upload (no download stream). */
  syncEndpoint?: string;
  now?: () => Date;
  subtle?: Ed25519Subtle;
  /** How often to re-read `/devices/config` while running. Default 5 minutes. */
  configIntervalMs?: number;
  /** How often to check whether the business's day has changed. Default 1 minute. */
  dayCheckIntervalMs?: number;
}

/** The signed-in staff member may not do this (the station should not have offered it). */
export class NotPermittedError extends Error {
  constructor(readonly permission: PermissionKey) {
    super(`Not allowed: needs ${permission}`);
    this.name = 'NotPermittedError';
  }
}

/**
 * The access key belongs to another account while this station still has unsent changes
 * (review CR3-7): they would be sent into the wrong business. The new registration is kept
 * (secure storage, across restarts: B-039); {@link StationData.wipeAndPair} completes it once
 * a manager wipes the station, or {@link StationData.finishPendingPairing} once the changes
 * have reached the office.
 */
export class AccountSwitchBlockedError extends Error {
  constructor(
    /** Commands still waiting to upload. */
    readonly pendingCount: number,
    /** The station they belong to. */
    readonly previous: PairedDevice,
    /** The station the key pairs with. */
    readonly next: PairedDevice,
  ) {
    super(
      `This station still has ${pendingCount} unsent ${pendingCount === 1 ? 'change' : 'changes'} for another business`,
    );
    this.name = 'AccountSwitchBlockedError';
  }
}

/**
 * A manager's approval of one action (D-048), from {@link StationData.approve}. Used once,
 * within {@link APPROVAL_TTL_MS}; `enqueue` accepts only approvals this station issued.
 */
export interface Approval {
  readonly approver: StaffMember;
  readonly approvedAt: string;
  readonly permission: PermissionKey;
}

/** How long an approval can wait before the action it approves is saved. */
export const APPROVAL_TTL_MS = 5 * 60_000;

/** The payload member a manager-PIN approval travels in (as on counts and write-offs). */
export interface ApprovalPayload {
  mode: 'manager_pin';
  approved_by_staff_id: string;
  approved_at: string;
}

/** Wiping and pairing needs a manager of the old account with this permission. */
export const WIPE_PERMISSION: PermissionKey = 'devices.manage';

/**
 * The permission a manager's PIN approval stands for, per command (D-048, D-100):
 *   - `stock.state_move`: `stock.adjust`, the move's own permission. The manager's approval
 *     stands in for it, so someone without it can make the move (UI3S-4).
 *   - `count.submit`: `stock.adjust` (an adjustment above the threshold); the person counting
 *     still needs `stock.count`.
 *   - `writeoff.request`: `writeoff.approve` (a write-off above the threshold); the person
 *     still needs `writeoff.request`.
 * Other commands take no approval.
 */
export function approvalPermissionFor(type: string): PermissionKey | null {
  switch (type) {
    case 'stock.state_move':
      return 'stock.adjust';
    case 'count.submit':
      return approverPermission('adjustment');
    case 'writeoff.request':
      return approverPermission('write_off');
    default:
      return null;
  }
}

/** Takes the next document number on this device (inside the command's transaction). */
export type NextDocNumber = (prefix: DocPrefix) => Promise<string>;

/**
 * A payload made inside the command's transaction, with document numbers (`SC-K7Q-00012`)
 * taken from the device's counter in that same transaction (plan §9.4): a crash keeps both
 * the number and the command, or neither.
 */
export type PayloadMaker = (next: NextDocNumber) => unknown;

export class StationData {
  readonly engine: SyncEngine;
  readonly auth: DeviceAuth;
  readonly staff: StaffSignIn;
  readonly status: Store<StationDataStatus>;
  readonly #api: ApiClient;
  readonly #appVersion: string;
  readonly #syncEndpoint: string | undefined;
  readonly #now: () => Date;
  readonly #subtle: Ed25519Subtle | undefined;
  readonly #configIntervalMs: number;
  /** The business's zone and cut-off: the synced account row's, else the defaults. */
  #businessDay: BusinessDaySettings = DEFAULT_BUSINESS_DAY;
  readonly #dayCheckIntervalMs: number;
  #dayTimer: ReturnType<typeof setInterval> | undefined;
  /** The business day the done-today stream is subscribed for. */
  #doneDay: string | undefined;
  readonly #approvals = new WeakSet<Approval>();
  readonly #enqueued = new Set<(envelope: CommandEnvelope) => void>();
  readonly #answered = new Set<(commandIds: string[]) => void>();
  #timer: ReturnType<typeof setInterval> | undefined;
  #unsubscribers: (() => void)[] = [];
  #connected = false;

  constructor(options: StationDataOptions) {
    // A native station never keeps its credentials in a development store (IndexedDB).
    assertSecureStoreFor(options.platform, options.store);
    this.engine = options.engine;
    this.#api = options.api;
    this.#appVersion = options.appVersion;
    this.#syncEndpoint = options.syncEndpoint;
    this.#now = options.now ?? (() => new Date());
    this.#subtle =
      options.subtle ?? (globalThis.crypto?.subtle as unknown as Ed25519Subtle | undefined);
    this.#configIntervalMs = options.configIntervalMs ?? 5 * 60_000;
    this.#dayCheckIntervalMs = options.dayCheckIntervalMs ?? 60_000;
    this.auth = new DeviceAuth({
      api: options.api,
      store: options.store,
      platform: options.platform,
      appVersion: options.appVersion,
      now: this.#now,
      // Any 426 (register, token) raises the gate before the error travels on (CR3-1).
      onUpgradeRequired: (notice) => this.#raiseGate(notice),
    });
    this.staff = new StaffSignIn({ engine: options.engine, now: this.#now });
    this.status = new Store<StationDataStatus>({
      engine: options.engine.getStatus(),
      pairing: { status: 'loading' },
      upgrade: { required: false },
      entitlement: { status: 'missing', banner: false, modules: [] },
      clockSkew: null,
      businessDay: {
        ...DEFAULT_BUSINESS_DAY,
        today: businessDayAt(DEFAULT_BUSINESS_DAY, this.#now()),
      },
      session: null,
      configProblem: null,
    });
  }

  /** Opens the local database, loads the pairing and, when paired, starts syncing. */
  async start(): Promise<void> {
    this.#unsubscribers.push(
      this.engine.subscribe((engine) => this.status.set({ engine })),
      this.auth.state.subscribe(({ pairing }) => this.status.set({ pairing })),
    );
    await this.engine.open();
    this.status.set({ engine: this.engine.getStatus() });
    // A hold a manager resolved in the Admin syncs down: its local entry goes (CR3-5).
    this.#unsubscribers.push(
      this.engine.watch<{ id: string }>(RESOLVED_HOLDS_SQL, [], (rows) => {
        if (rows.length > 0) void this.engine.writeTransaction(pruneResolvedHolds);
      }),
      // A held delivery a manager booked or voided stops reading "Held" (CR6-102).
      this.engine.watch<{ id: string }>(RESOLVED_HELD_RECEIPTS_SQL, [], (rows) => {
        if (rows.length > 0) void this.engine.writeTransaction(resolveHeldReceipts);
      }),
      // So do held batch actions and hand-overs (CR8B-4).
      this.engine.watch<{ id: string }>(RESOLVED_HELD_BATCH_ACTIONS_SQL, [], (rows) => {
        if (rows.length > 0) {
          void this.engine.writeTransaction((tx) => resolveHeldActions(tx, 'batch_action'));
        }
      }),
      this.engine.watch<{ id: string }>(RESOLVED_HELD_HANDOVERS_SQL, [], (rows) => {
        if (rows.length > 0) {
          void this.engine.writeTransaction((tx) => resolveHeldActions(tx, 'handover_action'));
        }
      }),
      // The account's zone and cut-off sync down (B-051): today, and the day the done-today
      // stream is subscribed for, follow them (back to the defaults if the row goes).
      this.engine.watch<BusinessDayRow>(BUSINESS_DAY_SQL, [], (rows) => {
        this.#businessDay = businessDaySettings(rows[0]);
        void this.#followBusinessDay();
      }),
    );
    let pairing = await this.auth.load();
    if (pairing.status === 'paired' && !(await this.#hasSequence(pairing.device))) {
      // Credentials survived, the local database didn't (e.g. an iOS reinstall): numbering
      // would start again at 0 and repeat lot codes. Pair again for a new device code (CR3-4).
      await this.auth.markLocalDataLost();
      pairing = this.auth.pairing;
    }
    await this.#refreshDerived();
    if (pairing.status === 'paired') await this.#ensureSequence(pairing.device);
    if (pairing.status === 'paired' || pairing.status === 'repair_required') {
      await this.#connect();
    }
    this.#timer = setInterval(() => void this.refreshConfig(), this.#configIntervalMs);
    // Today's finished batches of the branch, whichever station finished them (B-049). After
    // the pairing is loaded: nothing here may hold up sign-in.
    await this.#followBusinessDay();
    this.#dayTimer = setInterval(() => void this.#followBusinessDay(), this.#dayCheckIntervalMs);
  }

  async stop(): Promise<void> {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
    if (this.#dayTimer) clearInterval(this.#dayTimer);
    this.#dayTimer = undefined;
    for (const u of this.#unsubscribers.splice(0)) u();
    await this.engine.disconnect();
    this.#connected = false;
  }

  /**
   * Pairs this install (typed code + key, or a scanned `PAIR:` QR). Throws PairingError, or
   * {@link AccountSwitchBlockedError} when the key belongs to another account while changes
   * for this one wait. Joining another account (with nothing waiting) clears the station.
   */
  async pair(input: PairInput): Promise<PairedDevice> {
    const device = await this.auth.pair(input, {
      guard: async (next) => {
        const previous = this.auth.device;
        if (previous && previous.accountId !== next.accountId) {
          const pending = await this.pendingCommandCount();
          if (pending > 0) throw new AccountSwitchBlockedError(pending, previous, next);
          await this.#clear();
        }
        // The counter before the credentials (review CR8B-15): a crash between the two must
        // not leave credentials without their counter (that reads as lost data: re-pair).
        await this.#ensureSequence(next);
      },
    });
    return this.#paired(device);
  }

  /**
   * Wipes this station (every local row, **unsent changes included**) and completes the
   * pairing {@link pair} held back. A manager of the old account approves it with their PIN
   * (permission {@link WIPE_PERMISSION}).
   */
  async wipeAndPair(approverId: string, pin: string): Promise<ApprovalResult> {
    const previous = this.auth.device;
    if (!previous || !this.auth.pendingDevice) {
      throw new Error('There is no pairing waiting for a wipe');
    }
    const result = await this.staff.approve(approverId, pin, previous.branchId, WIPE_PERMISSION);
    if (!result.ok) return result;
    await this.#clear();
    await this.#ensureSequence(this.auth.pendingDevice); // before adopting it (CR8B-15)
    const device = await this.auth.adoptPending();
    if (device) await this.#paired(device);
    return result;
  }

  /**
   * A pairing with another business held back while changes wait (B-039): what it is and how
   * many changes still wait, or null. Survives restarts, so the key isn't stranded.
   */
  async pendingAccountSwitch(): Promise<AccountSwitchBlockedError | null> {
    const previous = this.auth.device;
    const next = this.auth.pendingDevice;
    if (!previous || !next) return null;
    return new AccountSwitchBlockedError(await this.pendingCommandCount(), previous, next);
  }

  /**
   * Completes the held-back pairing without a wipe once nothing waits to upload any more
   * (the changes reached the office): the old business's data is cleared, as for any switch.
   * Returns the new device, or the switch still blocked (changes still wait).
   */
  async finishPendingPairing(): Promise<PairedDevice | AccountSwitchBlockedError> {
    const blocked = await this.pendingAccountSwitch();
    if (!blocked) throw new Error('There is no pairing waiting');
    if (blocked.pendingCount > 0) return blocked;
    await this.#clear();
    const pending = this.auth.pendingDevice;
    if (pending) await this.#ensureSequence(pending); // before adopting it (CR8B-15)
    const device = await this.auth.adoptPending();
    if (!device) throw new Error('There is no pairing waiting');
    return this.#paired(device);
  }

  /** Commands waiting to upload (not counting other queued local changes). */
  async pendingCommandCount(): Promise<number> {
    return this.engine.queuedCommandCount();
  }

  /** Re-reads `/devices/config`: entitlement, minimum version, clock skew. Never throws. */
  async refreshConfig(): Promise<void> {
    if (this.auth.pairing.status !== 'paired') return;
    try {
      await refreshDeviceConfig({
        engine: this.engine,
        api: this.#api,
        auth: this.auth,
        appVersion: this.#appVersion,
        now: this.#now,
      });
      this.status.set({ configProblem: null });
    } catch (err) {
      const offline = (err as { isNetwork?: boolean }).isNetwork === true;
      const expected = err instanceof RepairRequiredError || err instanceof UpgradeRequiredError;
      if (!offline && !expected) this.status.set({ configProblem: errorMessage(err) });
    }
    await this.#refreshDerived();
    // A lifted gate or a fresh token: try the queue again.
    void this.engine.uploadNow();
  }

  /** Try to upload now (the Retry button, the network coming back). */
  async retry(): Promise<void> {
    await this.engine.uploadNow();
    await this.#refreshDerived();
  }

  async signIn(staffId: string, pin: string): Promise<SignInResult> {
    const device = this.auth.device;
    if (!device) return { ok: false, reason: 'unknown_staff' };
    const result = await this.staff.signIn(staffId, pin, device.branchId);
    if (result.ok) this.status.set({ session: result.session });
    return result;
  }

  signOut(): void {
    this.status.set({ session: null });
  }

  /** Whether the signed-in staff member may do `key` here. */
  can(key: PermissionKey): boolean {
    return sessionCan(this.status.get().session, key);
  }

  /** Staff here who may approve `key` with their PIN (D-048). */
  async approvers(key: PermissionKey): Promise<StaffMember[]> {
    const device = this.auth.device;
    return device ? this.staff.listApprovers(device.branchId, key) : [];
  }

  /**
   * A manager approves one action without signing in (D-048): their PIN, checked here (wrong
   * ones count toward the station's lock), and `key` at this branch. The signed-in person
   * stays signed in; pass the approval to {@link enqueue}.
   */
  async approve(
    staffId: string,
    pin: string,
    key: PermissionKey,
  ): Promise<{ ok: true; approval: Approval } | Exclude<ApprovalResult, { ok: true }>> {
    const device = this.auth.device;
    if (!device) return { ok: false, reason: 'unknown_staff' };
    const result = await this.staff.approve(staffId, pin, device.branchId, key);
    if (!result.ok) return result;
    const approval: Approval = {
      approver: result.approver,
      approvedAt: result.approvedAt,
      permission: key,
    };
    this.#approvals.add(approval);
    return { ok: true, approval };
  }

  /**
   * A station action: the command (from the signed-in staff member, on this device) goes
   * into the outbox with its local effect in one transaction, and uploads when it can.
   * `payload` may be a {@link PayloadMaker}, to put document numbers in it.
   *
   * With a manager's `approval` (D-048) the payload carries `approval` (who approved, when)
   * for the server. For a state move it stands in for the signed-in person's own
   * permission; for a count or a write-off above its threshold it doesn't: the person still
   * needs `stock.count` / `writeoff.request` ({@link approvalPermissionFor}).
   */
  async enqueue(
    type: string,
    payload: unknown,
    options: { effect?: LocalEffect; commandId?: string; approval?: Approval } = {},
  ): Promise<CommandEnvelope> {
    const device = this.auth.device;
    if (!device) throw new RepairRequiredError('not_paired');
    const session = this.status.get().session;
    if (!session) throw new Error('Nobody is signed in at this station');
    const def = STATION_REGISTRY.get(type);
    if (!def) {
      throw new InvalidCommandError('unknown_type', [{ path: 'type', message: `Unknown ${type}` }]);
    }
    const approval = options.approval && this.#useApproval(options.approval, type);
    const covered = approval !== undefined && approval.permission === def.permission;
    if (def.permission && !covered && !sessionCan(session, def.permission)) {
      throw new NotPermittedError(def.permission);
    }
    const draft = async (tx: LocalTx) => {
      const made =
        typeof payload === 'function'
          ? await (payload as PayloadMaker)((prefix) => nextDocNumber(tx, device.deviceId, prefix))
          : payload;
      const body =
        approval && made && typeof made === 'object'
          ? { ...made, approval: approvalPayload(approval) }
          : made;
      return {
        command_id: options.commandId ?? newId(),
        type,
        schema_version: def.versions.current,
        payload: body,
        device_id: device.deviceId,
        staff_id: session.staff.id,
        occurred_at: this.#now().toISOString(),
        app_version: this.#appVersion,
      };
    };
    const envelope = await this.engine.enqueueCommand(draft, options.effect);
    for (const listener of this.#enqueued) {
      try {
        listener(envelope);
      } catch (err) {
        console.error('An enqueue listener failed', err);
      }
    }
    return envelope;
  }

  /**
   * Calls `listener` after each command is saved on this station (e.g. the stock take in
   * progress notes moves at its location, CR4-6). Returns the unsubscribe.
   */
  onEnqueued(listener: (envelope: CommandEnvelope) => void): () => void {
    this.#enqueued.add(listener);
    return () => this.#enqueued.delete(listener);
  }

  /**
   * Calls `listener` with the ids of commands the server gave a final answer (applied,
   * held, refused, …), as each batch is answered. The stock figures change once a download
   * after that comes in (UI4S-204, D-057). Returns the unsubscribe.
   */
  onAnswered(listener: (commandIds: string[]) => void): () => void {
    this.#answered.add(listener);
    return () => this.#answered.delete(listener);
  }

  /** "OK" on the refused-changes notice: those stay in the Admin, not on this station. */
  async acknowledgeRejected(): Promise<void> {
    await this.engine.writeTransaction((tx) => acknowledgeRejected(tx, this.#now()));
  }

  /** "OK" on the notes notice (flagged commands): they stay in the Admin. */
  async acknowledgeFlagged(): Promise<void> {
    await this.engine.writeTransaction((tx) => acknowledgeFlagged(tx, this.#now()));
  }

  /** The approval, if this station issued it for `type`'s permission and it is fresh; used once. */
  #useApproval(approval: Approval, type: string): Approval {
    if (!this.#approvals.has(approval)) throw new Error('That approval was not given here');
    this.#approvals.delete(approval);
    const permission = approvalPermissionFor(type);
    if (!permission) throw new Error(`A ${type} takes no manager approval`);
    if (approval.permission !== permission) throw new NotPermittedError(permission);
    if (this.#now().getTime() - Date.parse(approval.approvedAt) > APPROVAL_TTL_MS) {
      throw new Error('That approval has expired; ask the manager again');
    }
    return approval;
  }

  async #raiseGate(notice: UpgradeNotice) {
    try {
      await raiseUpgradeGate(this.engine, this.#appVersion, notice, this.#now());
    } catch (err) {
      console.error('Could not record the minimum-version gate', err);
    }
    await this.#refreshDerived();
  }

  /** Clears every local row before the station joins another account. */
  async #clear() {
    await this.engine.clearLocalData();
    this.#connected = false;
    this.signOut();
    // The subscriptions went with the data: the next account's station subscribes again.
    this.#doneDay = undefined;
    await this.#followBusinessDay();
  }

  /**
   * Keeps `status.businessDay` and the today streams on the business's current trading day
   * (B-049, B-051, B-054, WP7.3): sync-stream SQL has no clock, so the station names the day, and
   * moves to the next one at the account's cut-off (midnight unless it says otherwise).
   * Yesterday's subscriptions are let go (the production queue shows today's finished
   * batches, Receiving today's deliveries, Orders today's orders to hand over).
   */
  async #followBusinessDay(): Promise<void> {
    const settings = this.#businessDay;
    const day = businessDayAt(settings, this.#now());
    this.status.set({ businessDay: { ...settings, today: day } });
    if (day === this.#doneDay) return;
    this.#doneDay = day;
    // Earlier days' answered hand-overs go with their orders (CR7-9: the customer's name).
    void this.engine
      .writeTransaction(async (tx) => {
        await pruneHandoverActions(tx, day);
        // And batch actions a manager resolved on an earlier day (CR8B-4).
        await pruneResolvedBatchActions(tx, day);
      })
      .catch((err: unknown) => console.error('Could not prune earlier hand-overs', err));
    try {
      await this.engine.setStreamSubscriptions([
        { stream: PRODUCTION_DONE_STREAM, params: { day } },
        { stream: RECEIVING_DONE_STREAM, params: { day } },
        // Today's orders to hand over (WP7.3).
        { stream: ORDERS_HANDOVER_STREAM, params: { day } },
      ]);
    } catch (err) {
      // Tried again on the next check.
      this.#doneDay = undefined;
      console.error('Could not subscribe to today’s finished batches, deliveries and orders', err);
    }
  }

  async #paired(device: PairedDevice): Promise<PairedDevice> {
    await this.#ensureSequence(device);
    this.signOut();
    await this.#connect(true);
    return device;
  }

  /** Re-reads the gate, entitlement and clock skew into `status`. */
  async #refreshDerived(): Promise<void> {
    const gate = await readUpgradeGate(this.engine, this.#appVersion);
    const config = await readStoredConfig(this.engine);
    const entitlement = this.#subtle
      ? await evaluateEntitlement(this.engine, this.#subtle, this.#now())
      : this.status.get().entitlement;
    this.status.set({
      upgrade: gate
        ? {
            required: true,
            reason: gate.reason,
            ...(gate.minAppVersion ? { minAppVersion: gate.minAppVersion } : {}),
          }
        : { required: false },
      entitlement,
      clockSkew: config?.clockSkew ?? null,
    });
  }

  async #hasSequence(device: PairedDevice): Promise<boolean> {
    const row = await this.engine.getOptional<{ id: string }>(
      'SELECT id FROM device_sequence WHERE id = ?',
      [device.deviceId],
    );
    return row !== null;
  }

  async #ensureSequence(device: PairedDevice) {
    await this.engine.writeTransaction((tx) =>
      initDeviceSequence(tx, device.deviceId, device.deviceCode),
    );
  }

  async #connect(force = false) {
    if (this.#connected && !force) return;
    const uploadData = createCommandUploader({
      api: this.#api,
      auth: this.auth,
      appVersion: this.#appVersion,
      now: this.#now,
      onAnswered: (response) => {
        const ids = response.results
          .filter((r) => isStoredStatus(r.status) && typeof r.command_id === 'string')
          .map((r) => r.command_id as string);
        if (ids.length === 0) return;
        for (const listener of this.#answered) {
          try {
            listener(ids);
          } catch (err) {
            console.error('An answer listener failed', err);
          }
        }
      },
    });
    const endpoint = this.#syncEndpoint;
    const connector: SyncConnector = {
      fetchCredentials: async () => {
        if (!endpoint) return null;
        try {
          const token = await this.auth.getToken();
          // Its expiry by this device's clock, so the sync SDK refreshes it on time (WP8.1).
          const expiresAt = this.auth.tokenExpiresAt ?? new Date(token.expires_at);
          return { endpoint, token: token.token, expiresAt };
        } catch (err) {
          // Pair again, or update first: nothing to download until then (the gate is up).
          if (err instanceof RepairRequiredError || err instanceof UpgradeRequiredError) {
            return null;
          }
          throw err;
        }
      },
      uploadData: async (engine) => {
        try {
          await uploadData(engine);
        } finally {
          await this.#refreshDerived();
        }
      },
    };
    await this.engine.connect(connector, { stream: endpoint !== undefined });
    this.#connected = true;
    await this.refreshConfig();
  }
}

function approvalPayload(approval: Approval): ApprovalPayload {
  return {
    mode: 'manager_pin',
    approved_by_staff_id: approval.approver.id,
    approved_at: approval.approvedAt,
  };
}
